// RightPanel — orchestrator. Tab bodies extracted to ./RightPanelTabs/
import React from 'react';
import {
  Box, Typography, Button, Tabs, Tab, Dialog, IconButton,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Chip, CircularProgress, Divider, Paper,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  CallSplit as CallSplitIcon,
  Delete as DeleteIcon, Edit as EditIcon,
  ExpandMore as ExpandMoreIcon, ViewModule as TemplateIcon,
  Close as CloseIcon, Warning as WarningIcon,
  Info as InfoIcon, Verified as VerifiedIcon,
} from '@mui/icons-material';
import { ELEMENT_TYPES, TEMPLATE_TYPE_OPTIONS, LAYOUT_TYPES } from '../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../utils/offerLayoutConfigs';
import {
  BRANDS_EDITOR_RIGHT_PANEL_WIDTH,
  BRANDS_EDITOR_SAVED_COL_WIDTH,
} from '../../../utils/offerDeviceConstants';
import { useOfferBuilder } from '../OfferBuilderContext';
import ContentTab from './RightPanelTabs/ContentTab';
import StyleTab from './RightPanelTabs/StyleTab';
import AddElementTab from './RightPanelTabs/AddElementTab';
import SavedElementsColumn from './RightPanelTabs/SavedElementsColumn';

const RightPanel = () => {
  const {
    // Layout / mode
    isMobile,
    isBrandsPage,
    templatesOnly,
    customElementsOnly,
    paginationEnabled,
    isEditingMode,
    forceRender,

    // Selected element state
    selectedElement,
    selectedElementContext,
    selectedSubElement,
    setSelectedElement,
    setSelectedElementContext,
    setSelectedSubElement,
    tabValue,
    setTabValue,
    rightPanelOpen,
    setRightPanelOpen,

    // Elements / pages
    elements,
    pages,
    currentPageIndex,
    pageSettings,

    // Operations
    updateElement,
    addElement,
    moveToNewPage,
    handleImageUpload,
    handleThumbnailUpload,
    handleMediaUploadForElement,

    // Inline edit
    contentTabTempValue,
    setContentTabTempValue,
    buttonContentTabTempValue,
    setButtonContentTabTempValue,
    buttonUrlTabTempValue,
    setButtonUrlTabTempValue,
    saveContentTabEdit,
    cancelContentTabEdit,
    handleContentTabKeyPress,
    saveButtonContentTabEdit,
    cancelButtonContentTabEdit,
    handleButtonContentTabKeyPress,
    saveButtonUrlTabEdit,
    cancelButtonUrlTabEdit,
    handleButtonUrlTabKeyPress,

    // Local right panel state
    listItemTextValues,
    setListItemTextValues,
    localRatingText,
    setLocalRatingText,
    renameElementId,
    setRenameElementId,
    renameElementValue,
    setRenameElementValue,

    // Color / font helpers
    getCurrentFontFamily,
    getInlineBackgroundColors,
    getInlineTextColors,

    // Saved elements
    savedOfferElements,
    filteredSavedElements,
    setFilteredSavedElements,
    loadSavedOfferElements,
    loadSavedElement,
    deleteSavedElement,
    editSavedElement,

    // Brand / offer
    selectedBrand,
    selectedOffer,
    brands,
    offers,
    setSelectedBrand,
    setSelectedOffer,
    loadOffersForBrand,

    // Status / confirm dialogs
    setShowStatusAlert,
    setStatusMessage,
    setStatusTitle,
    showConfirmDialog,
    setShowConfirmDialog,
    confirmDialogTitle,
    confirmDialogMessage,
    confirmAction,
    setConfirmAction,
    setConfirmDialogTitle,
    setConfirmDialogMessage,
    showRenameDialog,
    setShowRenameDialog,

    // Layout selector dialog
    showLayoutSelector,
    setShowLayoutSelector,
    targetZone,
    setTargetZone,

    // Save element dialog
    showSaveElementDialog,
    setShowSaveElementDialog,
    isUpdatingElement,
    setIsUpdatingElement,
    currentElementName,
    setCurrentElementName,
    selectedSavedElementId,
    setSelectedSavedElementId,
    setSaveType,
    saveType,
    saveCurrentElement,

    // Load template alert
    showLoadTemplateAlert,
    setShowLoadTemplateAlert,
    loadTemplateError,
    showReplaceConfirm,
    setShowReplaceConfirm,
    pendingTemplateData,
    loadReplaceTemplate,

    // Status alert
    showStatusAlert,
    statusMessage,
    statusTitle,

    // Prompt dialog
    showPromptDialog,
    setShowPromptDialog,
    promptDialogTitle,
    promptDialogMessage,
    promptDialogValue,
    setPromptDialogValue,
    promptAction,
    setPromptAction,

    // Save template dialog
    showSaveDialog,
    setShowSaveDialog,
    saveTemplate,

    // Templates list dialog
    showTemplatesList,
    setShowTemplatesList,
    templates,
    loadTemplate,
    deleteTemplate,
    setOffers,
    setForceRender,
    // missing from original extract
    imageUploading,
    uploadingElementId,
    savedTemplates,
    setSavedTemplates,
    preBuiltTemplates,
    loadPreBuiltTemplate,
    currentTemplateId,
    setCurrentTemplateId,
    templateName,
    setTemplateName,
    templateType,
    setTemplateType,
    templateSubtype,
    setTemplateSubtype,
    groupTemplatesByType,
    addLayout,
    showBrandOfferAlert,
    setShowBrandOfferAlert,
    renameSavedElement,
    setCurrentPageIndex,
    setElements,
    setNextPageId,
    setPageSettings,
    setPages,
    setPaginationEnabled,
    setPendingTemplateData,
  } = useOfferBuilder();

  return (
    <>
            {/* Right Panel - Advanced Style Editor - Available in both contexts when rightPanelOpen is true */}
            {rightPanelOpen && !isMobile && (selectedElement || tabValue === 2) && (
              <Box sx={{
                width: (isBrandsPage && templatesOnly)
                  ? BRANDS_EDITOR_RIGHT_PANEL_WIDTH
                  : customElementsOnly
                    ? 260
                    : 260,
                flexShrink: 0,
                minWidth: 0,
                borderLeft: '1px solid #e0e0e0',
                backgroundColor: '#fafafa',
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 0.5,
                pb: 20,
                height: '100%',
                maxHeight: 'calc(100vh - 50px)',
                position: 'relative'
              }}>
                {selectedElement && (
                  <Box sx={{ mb: 1, p: 1, backgroundColor: '#e8f5e8', borderRadius: 1, border: '2px solid #4caf50' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2e7d32' }}>
                      ✏️ Editing: {selectedElement?.type?.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Changes will only affect this specific element
                    </Typography>
                  </Box>
                )}

                {/* Page Management Section - Always visible when pagination is enabled and element is selected */}
                {(() => {
                  return paginationEnabled && selectedElement;
                })() && (
                    <Box sx={{
                      mb: 1,
                      p: 1,
                      backgroundColor: '#f0f8ff',
                      border: '1px solid #1976d2',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        📄 Page Management
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CallSplitIcon />}
                        onClick={() => {
                          console.log('🔴 Move to New Page button clicked!');
                          console.log('🔍 Current state:', {
                            paginationEnabled,
                            isEditingMode,
                            selectedElement: selectedElement?.id,
                            currentPageIndex,
                            pagesLength: pages.length
                          });
                          moveToNewPage();
                        }}
                        sx={{ width: '100%', mb: 1 }}
                      >
                        Move to New Page
                      </Button>


                    </Box>
                  )}

                <Tabs
                  value={tabValue}
                  onChange={(e, v) => {
                    setTabValue(v);
                    if (v === 2 && !rightPanelOpen) {
                      setRightPanelOpen(true);
                    }
                  }}
                  sx={{
                    mb: 2,
                    minHeight: 32,
                    '& .MuiTab-root': {
                      fontSize: '0.75rem',
                      minHeight: 32,
                      py: 0.5,
                      px: 1,
                      minWidth: 'auto',
                      fontWeight: 700
                    }
                  }}
                >
                  <Tab label="Content" />
                  <Tab label="Style" />
                  {isBrandsPage && <Tab label="Add new" />}
                </Tabs>

                {/* Content Tab */}
                {/* Content Tab */}
                {tabValue === 0 && selectedElement && <ContentTab />}

                {/* Style Tab */}
                {tabValue === 1 && selectedElement && <StyleTab />}

                {/* Add Element Tab */}
                {tabValue === 2 && <AddElementTab />}
              </Box>
            )}

            {/* Fourth Column - Saved Offer Elements */}
            {!isMobile && selectedBrand && selectedOffer && <SavedElementsColumn />}

      {/* Layout Selector Dialog */}
      < Dialog
        open={showLayoutSelector}
        onClose={() => {
          console.log('❌ Layout selector dialog closed');
          setShowLayoutSelector(false);
          setTargetZone(null); // Clear target zone on close
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📐 Choose a Layout {targetZone ? '(Nested Layout)' : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {targetZone
              ? 'Select a layout structure to nest inside the current layout zone'
              : 'Select a layout structure, then drag elements into the zones'
            }
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1.5 }}>
            {Object.entries(LAYOUT_CONFIGS).slice(0, 5).map(([layoutType, config]) => (
              <Paper
                key={layoutType}
                elevation={1}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={() => addLayout(layoutType)}
              >
                <Typography variant="h6" gutterBottom>
                  {config.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {config.description}
                </Typography>

                {/* Visual representation */}
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  height: '60px',
                  flexWrap: layoutType === LAYOUT_TYPES.THREE_COLUMN ? 'wrap' : 'nowrap'
                }}>
                  {config.zones.map((zone, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: zone.width,
                        height: zone.height || '100%',
                        backgroundColor: '#e3f2fd',
                        border: '1px dashed #1976d2',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="caption" color="primary">
                        Zone {index + 1}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowLayoutSelector(false);
            setTargetZone(null); // Clear target zone on cancel
          }}>Cancel</Button>
        </DialogActions>
      </Dialog >

      {/* Save Element Dialog */}
      < Dialog
        open={showSaveElementDialog}
        onClose={() => {
          setShowSaveElementDialog(false);
          setCurrentElementName('');
          setIsUpdatingElement(false);
          setSelectedSavedElementId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isUpdatingElement ? '✏️ Update Saved Item' : '💾 Save for Reuse'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isUpdatingElement
              ? 'Update the selected saved item with current modifications'
              : 'Choose what to save for reuse in other designs'
            }
          </Typography>



          {/* Preview Section */}
          {elements.length > 0 && (
            <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Offer Elements Preview: {elements.length} Elements
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {elements.map(el => el.type).join(', ')}
              </Typography>
            </Box>
          )}



          <TextField
            fullWidth
            label="Offer Elements Name"
            value={currentElementName}
            onChange={(e) => setCurrentElementName(e.target.value)}
            placeholder="e.g., Complete Product Landing Page, Hero Section Elements"
            sx={{ mb: 1.5 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowSaveElementDialog(false);
            setCurrentElementName('');
            setIsUpdatingElement(false);
            setSelectedSavedElementId(null);
            setSaveType('template'); // Reset to default
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveCurrentElement}
            disabled={
              !currentElementName.trim() ||
              elements.length === 0
            }
          >
            {isUpdatingElement
              ? 'Update Saved Item'
              : 'Save Offer Elements'
            }
          </Button>
        </DialogActions>
      </Dialog >

      {/* Brand/Offer Validation Dialog */}
      < Dialog
        open={showBrandOfferAlert}
        onClose={() => setShowBrandOfferAlert(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" /> Missing Information
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ⚠️ Please select both a brand and offer before saving an element.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This helps organize your saved elements properly in the library.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBrandOfferAlert(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog >

      {/* Template Loading Validation Dialog */}
      < Dialog
        open={showLoadTemplateAlert}
        onClose={() => setShowLoadTemplateAlert(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" /> Template Interaction
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {loadTemplateError || 'Please select a template first.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLoadTemplateAlert(false)} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog >

      {/* Replace Content Confirmation Dialog */}
      < Dialog
        open={showReplaceConfirm}
        onClose={() => setShowReplaceConfirm(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" /> Replace Content?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            This will load "{pendingTemplateData?.name}" with {pendingTemplateData?.elements?.length} elements.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: 'error.main' }}>
            Warning: This will replace your current editor content. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplaceConfirm(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const data = pendingTemplateData;
              if (!data) return;

              const newElements = data.elements.map(element => ({
                ...element,
                id: Date.now() + Math.random()
              }));

              setElements(newElements);

              if (data.pagination && data.pagination.enabled) {
                const savedPagination = data.pagination;
                const reconstructedPages = [];
                let elementIndex = 0;

                for (let i = 0; i < savedPagination.pages.length; i++) {
                  const pageInfo = savedPagination.pages[i];
                  const pageElements = newElements.slice(elementIndex, elementIndex + pageInfo.elementCount);
                  reconstructedPages.push({ id: pageInfo.id, elements: pageElements });
                  elementIndex += pageInfo.elementCount;
                }

                setPages(reconstructedPages);
                setCurrentPageIndex(savedPagination.currentPageIndex || 0);
                setNextPageId(Math.max(...reconstructedPages.map(p => p.id)) + 1);
                setPaginationEnabled(true);
              } else {
                setPages([{ id: 1, elements: newElements }]);
                setCurrentPageIndex(0);
                setNextPageId(2);
                setPaginationEnabled(false);
              }

              if (data.pageSettings) setPageSettings(data.pageSettings);
              setSelectedSavedElementId(data.id);
              setCurrentElementName(data.name);
              setIsUpdatingElement(true);
              setShowReplaceConfirm(false);
              setPendingTemplateData(null);
            }}
            variant="contained"
            color="error"
          >
            Yes, Replace
          </Button>
        </DialogActions>
      </Dialog >

      <Dialog
        open={showStatusAlert}
        onClose={() => setShowStatusAlert(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: 300 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
          {statusTitle === 'Success' ? <VerifiedIcon color="success" /> : (statusTitle === 'Error' || statusTitle === 'Error Saving' ? <CloseIcon color="error" /> : (statusTitle === 'Missing Information' || statusTitle === 'Missing Thumbnails' ? <WarningIcon color="warning" /> : <InfoIcon color="primary" />))}
          {statusTitle}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
            {statusMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowStatusAlert(false)}
            variant="contained"
            color="primary"
            sx={{ px: 3, borderRadius: 2 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: 320 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
          <WarningIcon sx={{ color: '#ff9800' }} /> {confirmDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {confirmDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmAction) confirmAction();
              setShowConfirmDialog(false);
            }}
            variant="contained"
            color="primary"
            sx={{ px: 3, borderRadius: 2 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: 400 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
          <EditIcon color="primary" /> Rename Saved Item
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter a new name for this saved element:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="medium"
            value={renameElementValue}
            onChange={(e) => setRenameElementValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameElementValue.trim()) {
                renameSavedElement(renameElementId, renameElementValue);
                setShowRenameDialog(false);
              }
            }}
            placeholder="Enter element name"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowRenameDialog(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              renameSavedElement(renameElementId, renameElementValue);
              setShowRenameDialog(false);
            }}
            variant="contained"
            color="primary"
            disabled={!renameElementValue.trim()}
            sx={{ px: 3, borderRadius: 2 }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog
        open={showPromptDialog}
        onClose={() => setShowPromptDialog(false)}
      >
        <DialogTitle>{promptDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{promptDialogMessage}</Typography>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            variant="outlined"
            size="small"
            value={promptDialogValue}
            onChange={(e) => setPromptDialogValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (promptAction) promptAction(promptDialogValue);
                setShowPromptDialog(false);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (promptAction) promptAction(promptDialogValue);
              setShowPromptDialog(false);
            }}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentTemplateId ? '📝 Update Template' : '💾 Save Template'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {currentTemplateId ? 'Update your template with the current page design' : 'Save your current page design as a reusable template'}
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mb: 1.5, fontWeight: 500 }}>
            💡 Select a type and subtype to organize your template for easy discovery in the Brands section
          </Typography>
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Product Launch Page"
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              label="Type"
            >
              {Object.keys(TEMPLATE_TYPE_OPTIONS).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 1.5 }} disabled={!templateType}>
            <InputLabel>Subtype</InputLabel>
            <Select
              value={templateSubtype}
              onChange={(e) => setTemplateSubtype(e.target.value)}
              label="Subtype"
            >
              {templateType && TEMPLATE_TYPE_OPTIONS[templateType] ?
                TEMPLATE_TYPE_OPTIONS[templateType].map((subtype) => (
                  <MenuItem key={subtype} value={subtype}>
                    {subtype}
                  </MenuItem>
                )) : []
              }
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            This template will include {elements.length} elements and your page settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveTemplate}
            disabled={!templateName.trim() || !templateType || !templateSubtype}
            title={!templateName.trim() ? 'Enter template name' : !templateType ? 'Select template type' : !templateSubtype ? 'Select template subtype' : 'Save template'}
          >
            {currentTemplateId ? 'Update Template' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Templates List Dialog */}
      <Dialog
        open={showTemplatesList}
        onClose={() => setShowTemplatesList(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📋 Load Template
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose a template to load. This will replace your current design.
          </Typography>

          {/* Pre-built templates section */}
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🎨 Pre-built Templates
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
              {preBuiltTemplates.map((template) => (
                <Button
                  key={template.id}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => loadPreBuiltTemplate(template)}
                  startIcon={<TemplateIcon />}
                  color="primary"
                  sx={{ py: 0.75, px: 1.5 }}
                >
                  {template.name}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Divider */}
          <Divider sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Your Saved Templates
            </Typography>
          </Divider>

          {savedTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No templates saved yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first template by designing a page and clicking "Save Template"
              </Typography>
            </Box>
          ) : (
            (() => {
              const groupedTemplates = groupTemplatesByType(savedTemplates);
              return (
                <Box>
                  {Object.entries(groupedTemplates).map(([type, subtypes]) => {
                    const totalTemplates = Object.values(subtypes).reduce((sum, templates) => sum + templates.length, 0);
                    return (
                      <Accordion key={type} defaultExpanded sx={{ mb: 1.5 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            📁 {type} Templates ({totalTemplates})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            {Object.entries(subtypes).map(([subtype, templates]) => (
                              <Box key={subtype} sx={{ mb: 1.5 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    mb: 1.5,
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pl: 1
                                  }}
                                >
                                  📂 {subtype} ({templates.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pl: 2 }}>
                                  {templates.map((template) => (
                                    <Paper
                                      key={template._id}
                                      elevation={1}
                                      sx={{
                                        p: 1.5,
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        '&:hover': {
                                          borderColor: '#1976d2',
                                          backgroundColor: '#f5f5f5'
                                        }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                          {template.name}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteTemplate(template._id);
                                          }}
                                          sx={{ color: 'error.main' }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>

                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {template.description}
                                      </Typography>

                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                        Created: {new Date(template.createdAt).toLocaleDateString()}
                                      </Typography>

                                      <Button
                                        variant="contained"
                                        size="small"
                                        fullWidth
                                        onClick={() => loadTemplate(template._id)}
                                        sx={{ py: 0.75, px: 1.5 }}
                                      >
                                        Load Template
                                      </Button>
                                    </Paper>
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              );
            })()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplatesList(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RightPanel;
